'use client';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAuth } from 'src/auth/AuthContext';
import { LoginForm } from 'src/auth/components/LoginForm';
import { ResetPasswordForm } from 'src/auth/components/ResetPasswordForm';
import { ResetSentMessage } from 'src/auth/components/ResetSentMessage';
import { termsService } from 'src/db/terms.service';
import { userDocService } from 'src/db/userDoc.service';
import { ROUTES } from 'src/routes';
import { Terms } from 'src/term.interface';
import { TermsContent } from 'src/Terms';

gsap.registerPlugin(ScrollTrigger);

type FeatureSection = {
  title: string;
  description: string;
  videoSrc: string;
  alignLeft: boolean;
  videoWidth?: number;
  videoHeight?: number;
  badge?: string;
};

const features: FeatureSection[] = [
  {
    title: 'Live Coverage Visualization',
    description:
      'See your design come to life in real time. Watch coverage patterns update instantly as you adjust heads and zones, ensuring perfect water distribution before you commit.',
    videoSrc: '/videos/arc.mp4',
    alignLeft: true,
    videoWidth: 1088,
    videoHeight: 778,
    badge: 'Real-time',
  },
  {
    title: 'Precision Drawing Tools',
    description:
      'Draw property boundaries, zones, and pipe networks with pixel-perfect accuracy. Every line, curve, and shape snaps to your exact specifications.',
    videoSrc: '/videos/drawing.mp4',
    alignLeft: false,
    videoWidth: 1280,
    videoHeight: 914,
    badge: 'CAD-grade',
  },
  {
    title: 'Smart Selection & Zoning',
    description:
      'Select and modify multiple elements at once with intuitive lasso tools. Group heads, adjust zones, and reorganize your design with effortless precision.',
    videoSrc: '/videos/lasso-zone.mp4',
    alignLeft: true,
    videoWidth: 1280,
    videoHeight: 818,
    badge: 'Intuitive',
  },
  {
    title: 'Grid-Based Analysis',
    description:
      'Analyze coverage patterns with precision. Our grid system reveals exactly where water lands, helping you optimize every square foot of your design.',
    videoSrc: '/videos/grid.mp4',
    alignLeft: false,
    videoWidth: 1280,
    videoHeight: 1158,
    badge: 'Analytics',
  },
];

const stats = [
  { value: '10x', label: 'Faster designs' },
  { value: '100%', label: 'Cloud-based' },
  { value: '0', label: 'CAD skills needed' },
];

const steps = [
  {
    number: '01',
    title: 'Upload Your Basemap',
    description:
      'Import satellite imagery, PDFs, or jpg files. Scale it once and start designing.',
  },
  {
    number: '02',
    title: 'Place & Configure',
    description:
      'Drop heads, draw pipes, define zones. The system handles hydraulic calculations automatically.',
  },
  {
    number: '03',
    title: 'Export & Install',
    description:
      'Generate permit-ready plans, parts lists, and easy to understand diagrams for install.',
  },
];

const benefits = [
  {
    title: 'Automatic Hydraulics',
    description: 'GPM, PSI, pipe sizing—calculated in real time as you design.',
  },
  {
    title: 'Parts Lists',
    description:
      'Accurate material takeoffs generated from your design automatically.',
  },
  {
    title: 'Professional Output',
    description: 'Export presentation-ready PDFs your clients will love.',
  },
  {
    title: 'Cloud Sync',
    description:
      'Access your designs anywhere. No software to install or update.',
  },
];

export default function FeatureLanding() {
  const { signup, login, user, loaded } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignupDialogOpen, setIsSignupDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [terms, setTerms] = useState<Terms | null>(null);
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isResetMode, setIsResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [loadedVideos, setLoadedVideos] = useState<Set<number>>(new Set());
  const videoRefs = useRef<(HTMLDivElement | null)[]>([]);
  const textRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isSignupDialogOpen && !terms) {
      setLoadingTerms(true);
      (async () => {
        const fetchedTerms = await termsService.fetchTerms();
        setTerms(fetchedTerms);
        setLoadingTerms(false);
      })();
    }
  }, [isSignupDialogOpen, terms]);

  // Lazy load videos
  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    videoRefs.current.forEach((ref, index) => {
      if (!ref) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setLoadedVideos((prev) => new Set(prev).add(index));
            observer.disconnect();
          }
        },
        {
          rootMargin: '200px',
          threshold: 0.01,
        },
      );

      observer.observe(ref);
      observers.push(observer);
    });

    return () => {
      observers.forEach((obs) => obs.disconnect());
    };
  }, []);

  // GSAP animations
  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const ctx = gsap.context(() => {
      // Animate video holders with scroll scrubbing
      videoRefs.current.forEach((ref, index) => {
        if (!ref) return;

        gsap.fromTo(
          ref,
          {
            // opacity: 0,
            scale: 0.9,
          },
          {
            // opacity: 1,
            scale: 1,
            duration: 0.8,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: ref,
              start: 'top 80%',
              end: 'top 50%',
              toggleActions: 'play none none reverse',
            },
          },
        );

        // Scroll scrubbing - move video up as user scrolls down
        gsap.to(ref, {
          ease: 'none',
          scrollTrigger: {
            trigger: ref,
            start: 'top bottom',
            end: 'bottom top',
          },
        });
      });

      // Animate text sections
      textRefs.current.forEach((ref, index) => {
        if (!ref) return;

        gsap.fromTo(
          ref,
          {
            opacity: 0,
            // x: features[index]?.alignLeft ? -30 : 30,
            y: 30,
          },
          {
            opacity: 1,
            x: 0,
            duration: 0.8,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: ref,
              start: 'top 80%',
              end: 'top 50%',
              toggleActions: 'play none none reverse',
            },
          },
        );
      });
    }, containerRef);

    return () => {
      ctx.revert();
    };
  }, []);

  // const handleSignupClick = () => {
  //   if (user) {
  //     window.location.href = ROUTES.APP;
  //     return;
  //   }
  //   setIsSignupDialogOpen(true);
  // };

  const handleLoginClick = () => {
    if (user) {
      window.location.href = ROUTES.APP;
      return;
    }
    setIsLoginDialogOpen(true);
  };

  const handleCloseSignupDialog = () => {
    if (!isLoading) {
      setIsSignupDialogOpen(false);
      setPassword('');
      setTermsAccepted(false);
      setError(null);
      setFirstName('');
      setEmail('');
    }
  };

  const handleCloseLoginDialog = () => {
    if (loginLoading) return;
    setIsLoginDialogOpen(false);
    setLoginError('');
    setIsResetMode(false);
    setResetSent(false);
    setResetEmail('');
  };

  const handleResetPassword = async (email: string) => {
    setLoginLoading(true);
    setLoginError('');
    try {
      await userDocService.resetPassword(
        email,
        () => {
          setResetEmail(email);
          setResetSent(true);
        },
        (err: any) => {
          setLoginError(err?.message || 'An error occurred');
        },
      );
      setResetEmail(email);
      setResetSent(true);
    } catch (err: any) {
      setLoginError(err?.message || 'An error occurred');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setIsResetMode(false);
    setResetSent(false);
    setLoginError('');
  };

  const handleSignupFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      setError('Please enter your first name and email.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setError(null);
    setIsSignupDialogOpen(true);
  };

  const handleSignupDialogSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim()) {
      setError('Please enter a password.');
      return;
    }

    if (!termsAccepted) {
      setError('Please agree to the Terms of Service to continue.');
      return;
    }

    if (!terms?.version) {
      setError('Terms not found. Please try again.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await signup(email, password, terms.version);
      window.location.href = ROUTES.APP;
    } catch (err) {
      console.error('Signup error:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to sign up. Please try again.';
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const handleLaunchWorkbench = () => {
    window.location.href = ROUTES.APP;
  };

  const handleLoginSubmit = async (
    emailValue: string,
    passwordValue: string,
  ) => {
    if (!login) return;
    setLoginLoading(true);
    setLoginError('');
    try {
      await login(emailValue, passwordValue);
      window.location.href = ROUTES.APP;
    } catch (err: any) {
      setLoginError(err?.message || 'An error occurred');
    } finally {
      setLoginLoading(false);
    }
  };
  const handleLaunchClick = (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      setError('Please enter your first name and email.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setError(null);
    setIsSignupDialogOpen(true);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: '#fff',
        p: 0,
        fontFamily: '"Manrope", sans-serif',
        '& .MuiTypography-root': {
          fontFamily: '"Manrope", sans-serif',
        },
        '& button': {
          fontFamily: '"Manrope", sans-serif',
        },
        '& input': {
          fontFamily: '"Manrope", sans-serif',
        },
        '& textarea': {
          fontFamily: '"Manrope", sans-serif',
        },
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 26,
          px: { xs: 2, md: 4 },
          py: 1.5,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        {!loaded ? (
          <>
            <Button variant="contained" color="primary" disabled>
              Loading...
            </Button>
            <Button color="primary" disabled>
              Loading...
            </Button>
          </>
        ) : user ? (
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              window.location.href = ROUTES.APP;
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 15,
            }}
          >
            Go to Dashboard
          </Button>
        ) : (
          <>
            {/* <Button
              variant="contained"
              color="primary"
              onClick={handleSignupClick}
            >
              Sign up free
            </Button> */}
            <Button
              color="primary"
              onClick={handleLoginClick}
              data-qaid="login-button-header"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: 15,
              }}
            >
              Login
            </Button>
          </>
        )}
      </Box>

      {/* HERO */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: { xs: 'auto', md: '90vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Background Video */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                'linear-gradient(135deg, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.75) 50%, rgba(255,255,255,0.55) 100%)',
            },
          }}
        >
          {/*   : Record hero-loop.mp4 - 15sec loop of placing heads, coverage appearing, lassoing into zone */}
          <Box
            component="video"
            autoPlay
            loop
            muted
            playsInline
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          >
            <source src="/videos/hero.webm" type="video/webm" />
            <source src="/videos/hero.mp4" type="video/mp4" />
          </Box>
        </Box>

        {/* Hero Content */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: { xs: 10, md: 14 },
            px: { xs: 3, md: 6 },
            width: '100%',
            maxWidth: 800,
            textAlign: 'center',
          }}
        >
          <Box
            component="img"
            src="logo-horiz.png"
            alt="Logo"
            sx={{ height: 28, mb: 5, opacity: 0.9 }}
          />
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 3,
              opacity: 0.9,
            }}
          >
            Questions? Call us:{' '}
            <Box
              component="a"
              href="tel:719-208-4004"
              aria-label="Call 719-208-4004"
              sx={{
                color: 'inherit',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              719-208-4004
            </Box>
          </Typography>

          <Typography
            variant="h1"
            sx={{
              fontWeight: 800,
              lineHeight: 1.1,
              color: 'text.primary',
              fontSize: { xs: 36, md: 56, lg: 64 },
              letterSpacing: '-0.02em',
              mb: 3,
            }}
          >
            You know{' '}
            <Box component="span" sx={{ color: 'primary.main' }}>
              irrigation
            </Box>
            .
            <br />
            Now automate the{' '}
            <Box component="span" sx={{ color: 'primary.main' }}>
              design
            </Box>
            .
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontSize: { xs: 17, md: 20 },
              lineHeight: 1.6,
              mb: 5,
              maxWidth: 560,
            }}
          >
            Professional irrigation design software that turns your expertise
            into engineering-grade plans. No CAD experience required.
          </Typography>

          {!loaded ? (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            </Box>
          ) : user ? (
            <Stack
              spacing={2.5}
              sx={{ width: '100%', maxWidth: 400, mt: 2, alignItems: 'center' }}
            >
              <Typography
                variant="body1"
                color="text.primary"
                sx={{ fontSize: 18, fontWeight: 500 }}
              >
                Welcome back!
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleLaunchWorkbench}
                sx={{
                  fontWeight: 600,
                  fontSize: 16,
                  py: 1.5,
                  px: 5,
                  textTransform: 'none',
                  borderRadius: 1,
                }}
              >
                Launch Workbench
              </Button>
            </Stack>
          ) : (
            <Box
              component="form"
              onSubmit={handleLaunchClick}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                gap: 1.5,
                width: '100%',
                maxWidth: 700,
                mt: 2,
              }}
            >
              <TextField
                placeholder="First name"
                required
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isLoading || isSignupDialogOpen}
                size="small"
                data-qaid="hero-firstname-input"
                sx={{
                  flex: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,255,255,0.9)',
                  },
                }}
              />

              <TextField
                placeholder="Email"
                type="email"
                required
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading || isSignupDialogOpen}
                size="small"
                data-qaid="hero-email-input"
                sx={{
                  flex: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,255,255,0.9)',
                  },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading || isSignupDialogOpen}
                data-qaid="hero-submit-button"
                sx={{
                  fontWeight: 600,
                  fontSize: 15,
                  py: 1.5,
                  px: 3,
                  textTransform: 'none',
                  borderRadius: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                Start Free Trial
              </Button>
            </Box>
          )}

          {error && !isSignupDialogOpen && (
            <Alert
              severity="error"
              variant="outlined"
              sx={{ mt: 2, maxWidth: 500 }}
            >
              {error}
            </Alert>
          )}

          {!user && loaded && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 3, fontSize: 14 }}
            >
              30-day free trial. No credit card required.
            </Typography>
          )}
        </Box>
      </Box>

      {/* Stats Bar */}
      <Box
        component="section"
        sx={{
          width: '100%',
          borderTop: '1px solid rgba(0, 0, 0, 0.06)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            px: { xs: 3, md: 6 },
            py: { xs: 5, md: 6 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 4, md: 12 },
          }}
        >
          {stats.map((stat, index) => (
            <Box
              key={index}
              sx={{
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 36, md: 48 },
                  fontWeight: 800,
                  color: 'primary.main',
                  lineHeight: 1,
                  letterSpacing: '-0.02em',
                }}
              >
                {stat.value}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 14, md: 15 },
                  fontWeight: 500,
                  color: 'text.secondary',
                  mt: 1,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* How It Works */}
      <Box
        component="section"
        sx={{
          width: '100%',
          backgroundColor: '#fff',
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            px: { xs: 3, md: 6 },
            py: { xs: 10, md: 14 },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 800,
              fontSize: { xs: 32, md: 44 },
              letterSpacing: '-0.02em',
              mb: 2,
              color: 'text.primary',
            }}
          >
            How it works
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: { xs: 17, md: 19 },
              color: 'text.secondary',
              mb: { xs: 6, md: 10 },
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            From basemap to build-ready plans in three simple steps
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 4, md: 6 },
            }}
          >
            {steps.map((step, index) => (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: 40, md: 48 },
                    fontWeight: 800,
                    color: 'primary.main',
                    opacity: 0.2,
                    lineHeight: 1,
                    mb: 2,
                  }}
                >
                  {step.number}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: 20, md: 22 },
                    mb: 1.5,
                    color: 'text.primary',
                  }}
                >
                  {step.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 15, md: 16 },
                    color: 'text.secondary',
                    lineHeight: 1.6,
                  }}
                >
                  {step.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Hydraulics Section - Full Width Video */}
      <Box
        component="section"
        sx={{
          width: '100%',
          backgroundColor: '#0a1628',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            maxWidth: 1400,
            mx: 'auto',
            px: { xs: 3, md: 6 },
            py: { xs: 10, md: 14 },
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: { xs: 5, md: 8 },
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', lg: '45%' },
            }}
          >
            <Typography
              component="span"
              sx={{
                display: 'inline-block',
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'primary.light',
                backgroundColor: 'rgba(25, 118, 210, 0.15)',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                mb: 3,
              }}
            >
              Automatic calculations
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: 32, md: 44 },
                letterSpacing: '-0.02em',
                mb: 3,
                color: '#fff',
                lineHeight: 1.15,
              }}
            >
              Design in real time
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 17, md: 19 },
                color: 'rgba(255,255,255,0.7)',
                lineHeight: 1.7,
                mb: 4,
              }}
            >
              Watch head placement, coverage updates, and pipe sizing happen
              automatically. The system calculates pressure losses through every
              valve, head, and pipe run, so you know your design will work
              before you ever break ground.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                'Pressure loss at every head',
                'Automatic pipe sizing',
                'Alerts when capacity is exceeded',
              ].map((item, i) => (
                <Box
                  key={i}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                >
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      color: '#fff',
                      fontWeight: 700,
                    }}
                  >
                    ✓
                  </Box>
                  <Typography
                    sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 16 }}
                  >
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Video/Image placeholder for hydraulic tree diagram */}
          <Box
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', lg: '55%' },
              aspectRatio: '16 / 10',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
              border: '1px solid rgba(255,255,255,0.1)',
              backgroundColor: '#1a2a40',
            }}
          >
            {/* REPLACE: Record hydraulics.mp4 - Show the hydraulic tree diagram, GPM/PSI updating as heads are added */}
            <Box
              component="video"
              src="/videos/track.mp4"
              autoPlay
              loop
              muted
              playsInline
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Feature Sections Header */}
      <Box
        component="section"
        sx={{
          width: '100%',
          backgroundColor: '#fff',
          borderTop: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            px: { xs: 3, md: 6 },
            pt: { xs: 10, md: 14 },
            pb: { xs: 4, md: 6 },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 800,
              fontSize: { xs: 32, md: 44 },
              letterSpacing: '-0.02em',
              mb: 2,
              color: 'text.primary',
            }}
          >
            Powerful features, simple interface
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: { xs: 17, md: 19 },
              color: 'text.secondary',
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            Everything you need to design professional irrigation systems
          </Typography>
        </Box>
      </Box>

      {features.map((feature, index) => (
        <Box
          key={index}
          component="section"
          sx={{
            width: '100%',
            backgroundColor: index % 2 === 1 ? 'rgba(0, 0, 0, 0.015)' : '#fff',
          }}
        >
          <Box
            sx={{
              maxWidth: 1200,
              mx: 'auto',
              display: 'flex',
              px: { xs: 3, md: 6 },
              py: { xs: 8, md: 12 },
              flexDirection: {
                xs: 'column',
                md: feature.alignLeft ? 'row' : 'row-reverse',
              },
              gap: { xs: 5, md: 8 },
              alignItems: 'center',
            }}
          >
            {/* Video Holder */}
            <Box
              ref={(el) => {
                videoRefs.current[index] = el as HTMLDivElement | null;
              }}
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: '58%' },
                aspectRatio:
                  feature.videoWidth && feature.videoHeight
                    ? `${feature.videoWidth} / ${feature.videoHeight}`
                    : '16 / 9',
                overflow: 'hidden',
                boxShadow:
                  '0 20px 60px rgba(0, 0, 0, 0.12), 0 8px 24px rgba(0, 0, 0, 0.08)',
                borderRadius: 2,
                border: '1px solid rgba(0, 0, 0, 0.08)',
                zIndex: 2,
                backgroundColor: '#f5f5f5',
              }}
            >
              {loadedVideos.has(index) ? (
                <Box
                  component="video"
                  src={feature.videoSrc}
                  autoPlay
                  loop
                  muted
                  playsInline
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    bgcolor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Loading...
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Text Content */}
            <Box
              ref={(el) => {
                textRefs.current[index] = el as HTMLDivElement | null;
              }}
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: '42%' },
              }}
            >
              {feature.badge && (
                <Typography
                  component="span"
                  sx={{
                    display: 'inline-block',
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: 'primary.main',
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    mb: 2,
                  }}
                >
                  {feature.badge}
                </Typography>
              )}
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 2.5,
                  color: 'text.primary',
                  fontSize: { xs: 26, md: 32 },
                  letterSpacing: '-0.01em',
                  lineHeight: 1.2,
                }}
              >
                {feature.title}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.7,
                  fontSize: { xs: 16, md: 18 },
                }}
              >
                {feature.description}
              </Typography>
            </Box>
          </Box>
        </Box>
      ))}

      {/* Professional Output Section */}
      <Box
        component="section"
        sx={{
          width: '100%',
          background: 'linear-gradient(180deg, #f8fafc 0%, #fff 100%)',
          borderTop: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            px: { xs: 3, md: 6 },
            py: { xs: 10, md: 14 },
          }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
            <Typography
              component="span"
              sx={{
                display: 'inline-block',
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'primary.main',
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                mb: 2,
              }}
            >
              One-click export
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: 32, md: 44 },
                letterSpacing: '-0.02em',
                mb: 2,
                color: 'text.primary',
              }}
            >
              Professional output, instantly
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 17, md: 19 },
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              Generate print-ready plans, accurate parts lists, and complete
              hydraulics packets with a single click
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 3, md: 4 },
            }}
          >
            {/* PDF Plans */}
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 2,
                backgroundColor: '#fff',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '4 / 3',
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  mb: 3,
                  backgroundColor: '#f5f5f5',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                }}
              >
                <Box
                  component="img"
                  src="img/design-sm.jpg"
                  alt="PDF design plans"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 18, md: 20 },
                  mb: 1,
                  color: 'text.primary',
                }}
              >
                Design Sheets
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 14, md: 15 },
                  color: 'text.secondary',
                  lineHeight: 1.6,
                }}
              >
                Multi-page PDFs with professional title blocks, ready to print
                or share with clients.
              </Typography>
            </Box>

            {/* Parts List */}
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 2,
                backgroundColor: '#fff',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '4 / 3',
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  mb: 3,
                  backgroundColor: '#f5f5f5',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                }}
              >
                <Box
                  component="img"
                  src="img/takeoff-sm.jpg"
                  alt="Parts list takeoff"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 18, md: 20 },
                  mb: 1,
                  color: 'text.primary',
                }}
              >
                Material Takeoffs
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 14, md: 15 },
                  color: 'text.secondary',
                  lineHeight: 1.6,
                }}
              >
                Accurate parts lists with quantities pulled directly from your
                design. No manual counting.
              </Typography>
            </Box>

            {/* Hydraulics Packet */}
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 2,
                backgroundColor: '#fff',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '4 / 3',
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  mb: 3,
                  backgroundColor: '#f5f5f5',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                }}
              >
                <Box
                  component="img"
                  src="img/hydraulic-sm.jpg"
                  alt="Hydraulics packet"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 18, md: 20 },
                  mb: 1,
                  color: 'text.primary',
                }}
              >
                Hydraulics Packet
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 14, md: 15 },
                  color: 'text.secondary',
                  lineHeight: 1.6,
                }}
              >
                Complete pressure calculations, zone breakdowns, and pipe sizing
                documentation.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Benefits Grid */}
      <Box
        component="section"
        sx={{
          width: '100%',
          backgroundColor: '#fff',
          borderTop: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            px: { xs: 3, md: 6 },
            py: { xs: 10, md: 14 },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 800,
              fontSize: { xs: 32, md: 44 },
              letterSpacing: '-0.02em',
              mb: 2,
              color: 'text.primary',
            }}
          >
            Built for professionals
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: { xs: 17, md: 19 },
              color: 'text.secondary',
              mb: { xs: 6, md: 10 },
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            The tools you need to deliver better projects, faster
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: { xs: 3, md: 4 },
            }}
          >
            {benefits.map((benefit, index) => (
              <Box
                key={index}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 2,
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                  backgroundColor: 'rgba(0, 0, 0, 0.01)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(25, 118, 210, 0.02)',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: 17, md: 18 },
                    mb: 1,
                    color: 'text.primary',
                  }}
                >
                  {benefit.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 14, md: 15 },
                    color: 'text.secondary',
                    lineHeight: 1.6,
                  }}
                >
                  {benefit.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* YouTube Video Section */}
      <Box
        component="section"
        sx={{
          width: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          borderTop: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <Box
          sx={{
            maxWidth: 1000,
            mx: 'auto',
            px: { xs: 3, md: 6 },
            py: { xs: 10, md: 14 },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              textAlign: 'center',
              color: 'text.primary',
              fontSize: { xs: 32, md: 44 },
              letterSpacing: '-0.02em',
            }}
          >
            See it in action
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: { xs: 17, md: 19 },
              color: 'text.secondary',
              mb: { xs: 5, md: 8 },
              maxWidth: 500,
              mx: 'auto',
            }}
          >
            Watch a full walkthrough of the design process
          </Typography>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '16 / 9',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: '#000000',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            }}
          >
            <Box
              component="iframe"
              src="https://www.youtube.com/embed/JPtFIjB6DeA?si=822X_Q-Ex91YxOhQ"
              title="Workbench Studio"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 0,
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Pricing Section */}
      <Box
        component="section"
        sx={{
          width: '100%',
          background: 'linear-gradient(180deg, #f8fafc 0%, #fff 100%)',
          borderTop: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            px: { xs: 3, md: 6 },
            py: { xs: 10, md: 14 },
          }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
            <Typography
              component="span"
              sx={{
                display: 'inline-block',
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'primary.main',
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                mb: 3,
              }}
            >
              Pricing
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: 32, md: 44 },
                letterSpacing: '-0.02em',
                mb: 2,
                color: 'text.primary',
              }}
            >
              Simple, transparent pricing
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 17, md: 19 },
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              Design it yourself with our software, or let us handle it for you
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: { xs: 3, md: 4 },
              maxWidth: 900,
              mx: 'auto',
              alignItems: 'start',
            }}
          >
            {/* Software subscription — primary */}
            <Box
              sx={{
                p: { xs: 4, md: 5 },
                borderRadius: 3,
                backgroundColor: '#fff',
                border: '2px solid',
                borderColor: 'primary.main',
                boxShadow: '0 8px 32px rgba(25, 118, 210, 0.12)',
                position: 'relative',
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'text.secondary',
                  mb: 1,
                }}
              >
                Software
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 20, md: 22 },
                  color: 'text.primary',
                  mb: 3,
                }}
              >
                Design it yourself
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: { xs: 44, md: 52 },
                    fontWeight: 800,
                    color: 'text.primary',
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                  }}
                >
                  $150
                </Typography>
                <Typography
                  sx={{
                    fontSize: 16,
                    color: 'text.secondary',
                    ml: 0.5,
                    fontWeight: 500,
                  }}
                >
                  /month
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: 14,
                  color: 'text.secondary',
                  mb: 4,
                }}
              >
                or $1,500/year (save $300)
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 4 }}>
                {[
                  'Unlimited designs',
                  'Real-time hydraulic calculations',
                  'Professional PDF exports',
                  'Material takeoffs & parts lists',
                  'Cloud sync across devices',
                  '30-day free trial',
                ].map((item, i) => (
                  <Box
                    key={i}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                  >
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        color: '#fff',
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      ✓
                    </Box>
                    <Typography
                      sx={{ color: 'text.secondary', fontSize: 15 }}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => {
                  if (user) {
                    window.location.href = ROUTES.APP;
                    return;
                  }
                  setIsSignupDialogOpen(true);
                }}
                sx={{
                  fontWeight: 600,
                  fontSize: 16,
                  py: 1.5,
                  textTransform: 'none',
                  borderRadius: 1,
                }}
              >
                Start Free Trial
              </Button>
            </Box>

            {/* Design service — add-on */}
            <Box
              sx={{
                p: { xs: 4, md: 5 },
                borderRadius: 3,
                backgroundColor: '#0a1628',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'rgba(255,255,255,0.5)',
                  mb: 1,
                }}
              >
                Add-on service
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 20, md: 22 },
                  color: '#fff',
                  mb: 3,
                }}
              >
                We design it for you
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: { xs: 44, md: 52 },
                    fontWeight: 800,
                    color: '#fff',
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                  }}
                >
                  $500
                </Typography>
                <Typography
                  sx={{
                    fontSize: 16,
                    color: 'rgba(255,255,255,0.5)',
                    ml: 0.5,
                    fontWeight: 500,
                  }}
                >
                  /first acre
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: 14,
                  color: 'rgba(255,255,255,0.5)',
                  mb: 4,
                }}
              >
                + $200 per additional acre
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 4 }}>
                {[
                  'Professional design by our team',
                  'Dropped into your account to own',
                  'Revise and export anytime',
                ].map((item, i) => (
                  <Box
                    key={i}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                  >
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        color: '#fff',
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      ✓
                    </Box>
                    <Typography
                      sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 15 }}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(25, 118, 210, 0.15)',
                  border: '1px solid rgba(25, 118, 210, 0.25)',
                  textAlign: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: 'primary.light',
                  }}
                >
                  25% off with a paid account
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Final CTA Section */}
      {!loaded ? null : user ? null : (
        <Box
          component="section"
          sx={{
            width: '100%',
            background:
              'linear-gradient(180deg, #fff 0%, rgba(25, 118, 210, 0.03) 100%)',
            borderTop: '1px solid rgba(0, 0, 0, 0.06)',
          }}
        >
          <Box
            sx={{
              maxWidth: 700,
              mx: 'auto',
              px: { xs: 3, md: 6 },
              py: { xs: 12, md: 16 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                textAlign: 'center',
                fontSize: { xs: 32, md: 48 },
                fontWeight: 800,
                letterSpacing: '-0.02em',
                color: 'text.primary',
              }}
            >
              Start designing today
            </Typography>
            <Typography
              sx={{
                textAlign: 'center',
                fontSize: { xs: 17, md: 20 },
                color: 'text.secondary',
                mb: { xs: 5, md: 6 },
                maxWidth: 500,
              }}
            >
              Join irrigation professionals who are already saving hours on
              every project
            </Typography>

            <Box
              component="form"
              onSubmit={handleSignupFormSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
                maxWidth: 400,
                p: { xs: 3, md: 5 },
                borderRadius: 3,
                backgroundColor: '#fff',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(0, 0, 0, 0.06)',
              }}
            >
              <Typography
                sx={{
                  textAlign: 'center',
                  fontSize: 15,
                  color: 'text.secondary',
                  mb: 1,
                }}
              >
                30-day free trial. No credit card required.
              </Typography>

              {error && !isSignupDialogOpen && (
                <Alert severity="error" variant="outlined" sx={{ mb: 1 }}>
                  {error}
                </Alert>
              )}

              <TextField
                label="First name"
                required
                fullWidth
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isLoading || isSignupDialogOpen}
                size="small"
              />

              <TextField
                label="Email"
                type="email"
                required
                fullWidth
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading || isSignupDialogOpen}
                size="small"
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading || isSignupDialogOpen}
                sx={{
                  fontWeight: 600,
                  fontSize: 16,
                  py: 1.5,
                  mt: 1,
                  textTransform: 'none',
                  borderRadius: 1,
                }}
              >
                Start Free Trial
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          width: '100%',
          borderTop: '1px solid rgba(0, 0, 0, 0.06)',
          backgroundColor: '#fff',
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            px: { xs: 3, md: 6 },
            py: { xs: 4, md: 5 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <Box
            component="img"
            src="logo-horiz.png"
            alt="Logo"
            sx={{ height: 20, opacity: 0.6 }}
          />
          <Typography
            sx={{
              fontSize: 14,
              color: 'text.secondary',
            }}
          >
            © {new Date().getFullYear()} Sprinkler Studio. All rights reserved.
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: 'text.secondary',
            }}
          >
            Call us:{' '}
            <Box
              component="a"
              href="tel:719-208-4004"
              aria-label="Call 719-208-4004"
              sx={{
                color: 'inherit',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              719-208-4004
            </Box>
          </Typography>
        </Box>
      </Box>

      {/* Signup Dialog */}
      <Dialog
        open={isSignupDialogOpen}
        onClose={handleCloseSignupDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create Your Account</DialogTitle>
        <DialogContent>
          <Box
            component="form"
            onSubmit={handleSignupDialogSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            {error && (
              <Alert severity="error" variant="outlined">
                {error}
              </Alert>
            )}

            <Box>
              <Stack spacing={1}>
                {/* <TextField
                label="First name"
                required
                fullWidth
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isLoading}
              /> */}
                <TextField
                  label="Email"
                  type="email"
                  required
                  fullWidth
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  data-qaid="signup-email-input"
                />
                <TextField
                  type="password"
                  label="Password"
                  required
                  fullWidth
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  data-qaid="signup-password-input"
                />
              </Stack>
            </Box>

            <Box
              sx={{
                height: 100,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 1,
                p: 1.5,
                bgcolor: 'grey.50',
              }}
            >
              {loadingTerms ? (
                <Typography variant="body2" color="text.secondary">
                  Loading terms...
                </Typography>
              ) : terms ? (
                <TermsContent terms={terms} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Terms not available. Please try again.
                </Typography>
              )}
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  disabled={isLoading || !terms?.version}
                  data-qaid="terms-checkbox"
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the Terms of Service above
                </Typography>
              }
            />

            <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
              <Button
                type="button"
                onClick={handleCloseSignupDialog}
                disabled={isLoading}
                variant="outlined"
                fullWidth
                sx={{ fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || !termsAccepted || !terms?.version}
                variant="contained"
                color="primary"
                fullWidth
                sx={{ fontWeight: 600 }}
                data-qaid="signup-button"
              >
                {isLoading ? 'Creating account...' : 'Create Account'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Login Dialog */}
      <Dialog
        open={isLoginDialogOpen}
        onClose={handleCloseLoginDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {resetSent
            ? 'Check your email'
            : isResetMode
              ? 'Reset Password'
              : 'Log in'}
        </DialogTitle>
        <DialogContent>
          {resetSent ? (
            <ResetSentMessage
              email={resetEmail}
              onBackToLogin={handleBackToLogin}
            />
          ) : isResetMode ? (
            <ResetPasswordForm
              onSubmit={handleResetPassword}
              onBackToLogin={handleBackToLogin}
              loading={loginLoading}
              error={loginError}
              onErrorClear={() => setLoginError('')}
            />
          ) : (
            <LoginForm
              onSubmit={handleLoginSubmit}
              onForgotPassword={() => setIsResetMode(true)}
              loading={loginLoading}
              error={loginError}
              onErrorClear={() => setLoginError('')}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
